import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = guest, object = authed
    const [activeClubId, setActiveClubId] = useState(() => localStorage.getItem("active_club_id") || "");
    const [activeSeasonId, setActiveSeasonId] = useState(() => localStorage.getItem("active_season_id") || "");
    const activeClubIdRef = useRef(activeClubId);
    const authGenerationRef = useRef(0);
    const mountedRef = useRef(false);

    activeClubIdRef.current = activeClubId;

    const updateActiveClub = useCallback((id) => {
        activeClubIdRef.current = id;
        setActiveClubId(id);
        if (id) localStorage.setItem("active_club_id", id);
        else localStorage.removeItem("active_club_id");
        setActiveSeasonId("");
        localStorage.removeItem("active_season_id");
    }, []);

    const fetchMe = useCallback(async () => {
        const requestGeneration = ++authGenerationRef.current;
        try {
            const { data } = await api.get("/auth/me");
            if (!mountedRef.current || requestGeneration !== authGenerationRef.current) return;
            const activeClubIsValid = data?.roles?.some((role) => role.club_id === activeClubIdRef.current);
            if ((!activeClubIdRef.current || !activeClubIsValid) && data?.roles?.[0]?.club_id) {
                updateActiveClub(data.roles[0].club_id);
            }
            setUser(data);
        } catch {
            if (mountedRef.current && requestGeneration === authGenerationRef.current) setUser(false);
        }
    }, [updateActiveClub]);

    useEffect(() => {
        mountedRef.current = true;
        fetchMe();
        return () => {
            mountedRef.current = false;
            authGenerationRef.current += 1;
        };
    }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        authGenerationRef.current += 1;
        updateActiveClub(data.user?.roles?.[0]?.club_id || "");
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        authGenerationRef.current += 1;
        try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
        setUser(false);
        setActiveClubId("");
        setActiveSeasonId("");
        localStorage.removeItem("active_club_id");
        localStorage.removeItem("active_season_id");
    };

    const updateActiveSeason = (id) => {
        setActiveSeasonId(id);
        if (id) localStorage.setItem("active_season_id", id);
        else localStorage.removeItem("active_season_id");
    };

    const roleInActiveClub = (() => {
        if (!user || !activeClubId) return null;
        const r = user.roles?.find((x) => x.club_id === activeClubId);
        return r?.role || null;
    })();

    const isSuperAdmin = !!user?.roles?.some((r) => r.role === "super_admin");
    const isClubAdmin = isSuperAdmin || roleInActiveClub === "club_admin";
    const canManageSports = isClubAdmin || roleInActiveClub === "coach" || roleInActiveClub === "staff";

    return (
        <AuthContext.Provider
            value={{
                user, setUser, login, logout,
                activeClubId, setActiveClubId: updateActiveClub,
                activeSeasonId, setActiveSeasonId: updateActiveSeason,
                roleInActiveClub, isSuperAdmin, isClubAdmin, canManageSports,
                refresh: fetchMe,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
